import React from "react";
import {
    X,
    ExternalLink,
    FileText,
} from "lucide-react";
import { Pagination } from "../../Common";

const DashboardTableModal = ({
    isOpen,
    onClose,
    title,
    data = [],
    columns = [],
    total = 0,
    perPage = 10,
    currPage = 1,
    totalPage = 1,
    updatePage,
    setCurrPage,
    setPerPage,
    loading = false,
}) => {
    if (!isOpen) return null;

    const formatValue = (key, value) => {

        if (!value) return "-";

        // Safety guard: if value is an object (not a React element), stringify it
        if (typeof value === "object" && !React.isValidElement(value)) {
            return JSON.stringify(value);
        }

        // Date Formatting
        if (
            key.includes("date") ||
            key.includes("visited_at") ||
            key.includes("created_at")
        ) {
            return (
                <span className="inline-flex items-center rounded-full bg-slate-100 px-3 py-1 text-sm text-slate-700">
                    {new Date(value).toLocaleString("en-IN", {
                        day: "numeric",
                        month: "numeric",
                        year: "numeric",
                        hour: "2-digit",
                        minute: "2-digit",
                        second: "2-digit",
                        hour12: true,
                    })}
                </span>
            );
        }

        // URL Formatting
        if (
            typeof value === "string" &&
            (value.startsWith("http://") ||
                value.startsWith("https://") ||
                value.startsWith("/"))
        ) {
            const finalUrl = value.startsWith("/")
                ? `${process.env.REACT_APP_FRONTEND_URL}${value}`
                : value;

            return (
                <a
                    href={finalUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group inline-flex items-center gap-2 text-teal-600 font-medium hover:text-teal-700 transition-all"
                >
                    <span className="max-w-[500px] truncate group-hover:underline">
                        {value}
                    </span>

                    <ExternalLink
                        size={15}
                        className="opacity-70 group-hover:translate-x-0.5 transition-all"
                    />
                </a>
            );
        }

        return value;
    };

    return (
        <div className="fixed inset-0 z-[99999] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">

            <div className="bg-white rounded-3xl shadow-2xl w-full max-w-5xl max-h-[90vh] overflow-hidden">

                {/* Header */}
                <div className="flex items-center justify-between border-b px-6 py-5 bg-gradient-to-r from-teal-600 to-cyan-600 text-white">

                    <div>
                        <h2 className="text-2xl font-bold !text-white">
                            {title}
                        </h2>

                        <p className="text-sm text-white/80 mt-1">
                            Total Records : {total || 0}
                        </p>
                    </div>

                    <button
                        onClick={onClose}
                        className="p-2 rounded-xl hover:bg-white/20 transition"
                    >
                        <X size={24} />
                    </button>
                </div>

                {/* Body */}
                <div className="overflow-y-auto max-h-[65vh] bg-gray-50">

                    {loading ? (

                        <div className="h-[500px] flex items-center justify-center">
                            <div className="text-center">

                                <div className="w-12 h-12 border-4 border-teal-500 border-t-transparent rounded-full animate-spin mx-auto"></div>

                                <p className="mt-4 text-gray-500">
                                    Loading...
                                </p>

                            </div>
                        </div>

                    ) : data?.length > 0 ? (

                        <table className="w-full">

                            <thead className="sticky top-0 bg-white shadow-sm z-10">

                                <tr>

                                    <th className="text-left px-6 py-4 text-xs uppercase tracking-wide font-semibold text-gray-500">
                                        #
                                    </th>

                                    {columns.map((column) => (
                                        <th
                                            key={column.key}
                                            className="text-left px-6 py-4 text-xs uppercase tracking-wide font-semibold text-gray-500"
                                        >
                                            {column.label}
                                        </th>
                                    ))}

                                </tr>

                            </thead>

                            <tbody>

                                {data.map((item, index) => (

                                    <tr
                                        key={index}
                                        className=" border-b border-gray-200 hover:bg-white hover:shadow-sm transition-all duration-200 "
                                    >

                                        <td className="px-6 py-4">

                                            <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-teal-50 text-teal-700 font-semibold text-sm">
                                                {index + 1}
                                            </span>

                                        </td>

                                        {columns.map((column) => (

                                            <td
                                                key={column.key}
                                                className="px-6 py-4 text-gray-700"
                                            >
                                                {formatValue(
                                                    column.key,
                                                    item[column.key]
                                                )}
                                            </td>

                                        ))}

                                    </tr>

                                ))}

                            </tbody>

                        </table>

                    ) : (

                        <div className="flex flex-col items-center justify-center py-24">

                            <FileText
                                size={70}
                                className="text-gray-300"
                            />

                            <h3 className="mt-5 text-xl font-semibold text-gray-600">
                                No Records Found
                            </h3>

                            <p className="text-gray-400 mt-2">
                                No data available for this section.
                            </p>

                        </div>

                    )}

                </div>
                {total > 0 && (
                    <div className=" border-t bg-white">
                        <Pagination
                            total={total}
                            perPage={perPage}
                            currPage={currPage}
                            prevPage={Math.max(1, currPage - 2)}
                            tPage={totalPage}
                            updatePage={updatePage}
                            setCurrPage={setCurrPage}
                            setPerPage={setPerPage}
                        />
                    </div>
                )}

            </div>



        </div>
    );
};

export default DashboardTableModal;